import { useState } from "react";
import {
  DataTable,
  DataTableExpandedRows,
  DataTableValueArray,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import * as api from "../../utils/Api.ts";
import ChartCard from "../widgets/graphs/ChartCard.tsx";
import { formatMoney, formatNumber } from "../widgets/graphs/chartTheme.ts";
import {
  FiltrosReporte,
  clean,
  estadoAprobacionColorPorNombre,
  formatDias,
  formatEtapa,
  formatFecha,
  inetInfo,
  useReporte,
} from "./reportesUtils.ts";

/* Shape returned by /reports/ciclo/trazabilidad */

interface LineaSolicitud {
  idLinea: number;
  estado: string;
  centroCosto: string;
  item: string;
  proveedor: string;
  cantidad: number;
  montoClp: number;
}

interface OrdenTrazabilidad {
  nroOrdenCompra: number;
  fechaOrden: string;
  estado: string;
  estadoInet: number | null;
  proveedor: string;
  // Raw ordenes_emitidas.dt_cre (bulk-load stamp for OCs before 2026-05-04):
  // not used for durations
  fechaEmision: string | null;
  // First Confirmada / Confirmada GG (states 8 / 10)
  fechaConfirmacion: string | null;
  // Reliable "entered INET" date: real emission, or the confirmation date
  // when the emission is the bulk-load stamp. null = not in INET yet
  fechaIngresoInet: string | null;
  fuenteFechaIngresoInet: "EMISION" | "CONFIRMACION" | null;
  montoClp: number;
  etapa: string;
  recepciones: { numero: string; fecha: string | null }[];
  facturas: unknown[];
}

/* Lead times per OC ------------------------------------------------- */

interface Plazo {
  dias: number | null;
  // Why there is no value (shown as tooltip)
  motivo?: string;
  // Extra context for a value (shown as tooltip)
  nota?: string;
}

const diasEntre = (desde: string, hasta: string) =>
  (new Date(hasta).getTime() - new Date(desde).getTime()) / 86400000;

// Dates from different sources can be a few hours out of order (e.g. an OC
// whose dt_cre is later than its own confirmation): under a day counts as 0
const sinNegativosMenores = (d: number) => (d < 0 && d > -1 ? 0 : d);

// OC creation -> entered INET (backend fechaIngresoInet)
const plazoInet = (o: OrdenTrazabilidad): Plazo => {
  if (!o.fechaIngresoInet)
    return { dias: null, motivo: "La OC aún no está en INET" };
  return {
    dias: sinNegativosMenores(diasEntre(o.fechaOrden, o.fechaIngresoInet)),
    nota:
      o.fuenteFechaIngresoInet === "CONFIRMACION"
        ? "Fecha de confirmación (emisión anterior a la carga en INET)"
        : "Fecha de emisión en INET",
  };
};

// OC creation -> first reception. Reception dates have no time of day, so a
// reception on the creation day counts as 0 days instead of a negative value
const plazoRecepcion = (o: OrdenTrazabilidad): Plazo => {
  const fechas = (o.recepciones ?? [])
    .map((r) => r.fecha)
    .filter((f): f is string => !!f);
  if (!fechas.length)
    return { dias: null, motivo: "Sin recepciones registradas" };
  const primera = fechas.reduce((a, b) => (new Date(a) < new Date(b) ? a : b));
  return { dias: sinNegativosMenores(diasEntre(o.fechaOrden, primera)) };
};

const PlazoCell = ({ plazo }: { plazo: Plazo }) =>
  plazo.dias === null ? (
    <span className="fd-sub" title={plazo.motivo}>
      -
    </span>
  ) : (
    <span className="fd-num" title={plazo.nota}>
      {formatDias(plazo.dias)}
    </span>
  );

// Solicitud row: the slowest of its OCs; tooltip lists every OC
const PlazoSolicitud = ({
  ordenes,
  calcular,
}: {
  ordenes: OrdenTrazabilidad[];
  calcular: (o: OrdenTrazabilidad) => Plazo;
}) => {
  if (!ordenes?.length) return <span className="fd-sub">-</span>;
  const plazos = ordenes.map((o) => ({ oc: o.nroOrdenCompra, ...calcular(o) }));
  const conValor = plazos.filter((p) => p.dias !== null);
  const detalle = plazos
    .map(
      (p) =>
        `OC ${p.oc}: ${p.dias === null ? `- (${p.motivo})` : formatDias(p.dias)}`,
    )
    .join("\n");
  if (!conValor.length) {
    return (
      <span className="fd-sub" title={detalle}>
        -
      </span>
    );
  }
  const max = Math.max(...conValor.map((p) => p.dias as number));
  return (
    <span className="fd-num" title={detalle}>
      {formatDias(max)}
      {plazos.length > 1 ? (
        <span className="fd-sub"> máx. {plazos.length} OC</span>
      ) : null}
    </span>
  );
};

interface SolicitudTrazabilidad {
  idSolicitud: number;
  fechaSolicitud: string;
  idEstado: number;
  estado: string;
  tipoDocumento: string;
  moneda: string;
  observaciones: string;
  idUsuario: number;
  usuario: string;
  montoClp: number;
  centrosCosto: string[];
  etapaActual: string;
  diasSolicitudAOrden: number | null;
  diasSolicitudAFactura: number | null;
  lineas: LineaSolicitud[];
  ordenes: OrdenTrazabilidad[];
}

interface TrazabilidadPage {
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
  data: SolicitudTrazabilidad[];
}

type BuscarPor = "idSolicitud" | "nroOrdenCompra";

interface TrazabilidadCardProps {
  filtros: FiltrosReporte;
  buscarPor: BuscarPor;
  // Bumped by the header "Actualizar" button to force a refetch
  refreshKey?: number;
}

// Rough severity by how far the solicitud got in the cycle
const etapaSeverity = (
  etapa: string,
): "success" | "info" | "warning" | "danger" | undefined => {
  const e = (etapa || "").toUpperCase();
  if (/RECHAZ|ANUL/.test(e)) return "danger";
  if (/CONTAB|FACTUR|PAGAD/.test(e)) return "success";
  if (/RECEP|EMITIDA|ORDEN/.test(e)) return "info";
  if (/PENDIENTE|APROB|CONFIRM/.test(e)) return "warning";
  return undefined;
};

// Approval state badge, styled like the maintenance tables
const EstadoTag = ({ estado }: { estado: string }) =>
  clean(estado) ? (
    <Tag
      value={clean(estado)}
      style={{
        backgroundColor: estadoAprobacionColorPorNombre(estado),
        color: "white",
      }}
    />
  ) : (
    <>-</>
  );

/**
 * Paginated (server side) solicitud → OC → recepción → factura traceability.
 */
export default function TrazabilidadCard({
  filtros,
  buscarPor,
  refreshKey = 0,
}: TrazabilidadCardProps) {
  // The page is tied to the filters it was chosen for: when the dashboard
  // filters change it falls back to 1 without an extra (stale) request
  const [pageState, setPageState] = useState({
    key: filtros.queryKey,
    page: 1,
  });
  const page = pageState.key === filtros.queryKey ? pageState.page : 1;
  const setPage = (p: number) =>
    setPageState({ key: filtros.queryKey, page: p });
  const [limit, setLimit] = useState(10);
  const [buscarInput, setBuscarInput] = useState<number | null>(null);
  const [buscar, setBuscar] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);

  const traza = useReporte<TrazabilidadPage>(
    () =>
      api.GET_REPORTES.getTrazabilidad({
        ...filtros.query,
        page,
        limit,
        // The card's own search narrows further; when empty it must not
        // erase the dashboard's N° solicitud / N° OC filter
        ...(buscar ? { [buscarPor]: buscar } : {}),
      }),
    `${filtros.queryKey}|${page}|${limit}|${buscar}|${refreshKey}`,
  );

  const aplicarBusqueda = () => {
    setPage(1);
    setBuscar(buscarInput);
  };

  const rows = traza.data?.data ?? [];

  const expansionTemplate = (s: SolicitudTrazabilidad) => (
    <div className="rp-expansion">
      <div>
        <h6>Líneas de la solicitud ({s.lineas?.length ?? 0})</h6>
        <DataTable value={s.lineas ?? []} size="small" className="fd-table">
          <Column
            header="Ítem"
            body={(l: LineaSolicitud) => clean(l.item) || "-"}
          />
          <Column
            header="Centro costo"
            body={(l: LineaSolicitud) => clean(l.centroCosto) || "-"}
          />
          <Column
            header="Proveedor"
            body={(l: LineaSolicitud) => clean(l.proveedor) || "-"}
          />
          <Column
            header="Cant."
            body={(l: LineaSolicitud) => (
              <span className="fd-num">{formatNumber(l.cantidad)}</span>
            )}
          />
          <Column
            header="Monto"
            body={(l: LineaSolicitud) => (
              <span className="fd-num">{formatMoney(l.montoClp)}</span>
            )}
          />
          <Column
            header="Estado"
            body={(l: LineaSolicitud) => <EstadoTag estado={l.estado} />}
          />
        </DataTable>
      </div>
      <div>
        <h6>Órdenes de compra ({s.ordenes?.length ?? 0})</h6>
        {s.ordenes?.length ? (
          <DataTable value={s.ordenes} size="small" className="fd-table">
            <Column
              header="N° OC"
              body={(o: OrdenTrazabilidad) => (
                <span className="fd-num">{o.nroOrdenCompra}</span>
              )}
            />
            <Column
              header="Proveedor"
              body={(o: OrdenTrazabilidad) => clean(o.proveedor) || "-"}
            />
            <Column
              header="Fecha OC"
              body={(o: OrdenTrazabilidad) => formatFecha(o.fechaOrden)}
            />
            <Column
              header="Monto"
              body={(o: OrdenTrazabilidad) => (
                <span className="fd-num">{formatMoney(o.montoClp)}</span>
              )}
            />
            <Column
              header="Estado"
              body={(o: OrdenTrazabilidad) => <EstadoTag estado={o.estado} />}
            />
            <Column
              header="Etapa"
              body={(o: OrdenTrazabilidad) => (
                <Tag
                  value={formatEtapa(o.etapa)}
                  severity={etapaSeverity(o.etapa)}
                />
              )}
            />
            <Column
              header="Estado INET"
              body={(o: OrdenTrazabilidad) =>
                o.estadoInet === null || o.estadoInet === undefined ? (
                  "-"
                ) : (
                  <Tag
                    value={inetInfo(o.estadoInet).label}
                    severity={inetInfo(o.estadoInet).severity}
                  />
                )
              }
            />
            <Column
              header="Días a INET"
              headerTooltip="Desde la creación de la OC hasta su ingreso a INET (emisión, o confirmación si la emisión es de la carga masiva)"
              body={(o: OrdenTrazabilidad) => (
                <PlazoCell plazo={plazoInet(o)} />
              )}
            />
            <Column
              header="Días a recepción"
              headerTooltip="Desde la creación de la OC hasta su primera recepción"
              body={(o: OrdenTrazabilidad) => (
                <PlazoCell plazo={plazoRecepcion(o)} />
              )}
            />
            <Column
              header="Recep. / Fact."
              body={(o: OrdenTrazabilidad) => (
                <span className="fd-num">
                  {o.recepciones?.length ?? 0} / {o.facturas?.length ?? 0}
                </span>
              )}
            />
          </DataTable>
        ) : (
          <div className="fd-note">Sin órdenes de compra asociadas.</div>
        )}
      </div>
      {clean(s.observaciones) ? (
        <p className="rp-obs">
          <strong>Observaciones:</strong> {clean(s.observaciones)}
        </p>
      ) : null}
    </div>
  );

  return (
    <ChartCard
      title="Trazabilidad solicitud → orden → recepción → factura"
      subtitle="Expandir una fila para ver sus líneas y órdenes de compra"
      error={traza.error}
      onRetry={traza.reload}
      height={160}
      actions={
        <div className="rp-search">
          <label htmlFor="rp-buscar">
            {buscarPor === "idSolicitud" ? "N° solicitud" : "N° OC"}
          </label>
          <InputNumber
            inputId="rp-buscar"
            value={buscarInput}
            onValueChange={(e) => setBuscarInput(e.value ?? null)}
            useGrouping={false}
            min={1}
            inputClassName="p-inputtext-sm"
            onKeyDown={(e) => {
              if (e.key === "Enter") aplicarBusqueda();
            }}
          />
          <Button
            icon="pi pi-search"
            size="small"
            text
            title="Buscar"
            onClick={aplicarBusqueda}
          />
          {buscar ? (
            <Button
              icon="pi pi-times"
              size="small"
              text
              title="Quitar búsqueda"
              onClick={() => {
                setBuscarInput(null);
                setPage(1);
                setBuscar(null);
              }}
            />
          ) : null}
        </div>
      }
    >
      <DataTable
        value={rows}
        dataKey="idSolicitud"
        lazy
        paginator
        first={(page - 1) * limit}
        rows={limit}
        totalRecords={traza.data?.total ?? 0}
        rowsPerPageOptions={[10, 25, 50]}
        onPage={(e) => {
          setLimit(e.rows);
          setPage(Math.floor(e.first / e.rows) + 1);
        }}
        loading={traza.loading}
        expandedRows={expanded}
        onRowToggle={(e) => setExpanded(e.data)}
        rowExpansionTemplate={expansionTemplate}
        size="small"
        stripedRows
        emptyMessage="Sin solicitudes para los filtros seleccionados"
        className="fd-table"
      >
        <Column expander style={{ width: "3rem" }} />
        <Column
          header="N° Sol."
          body={(s: SolicitudTrazabilidad) => (
            <span className="fd-num">{s.idSolicitud}</span>
          )}
        />
        <Column
          header="Fecha"
          body={(s: SolicitudTrazabilidad) => formatFecha(s.fechaSolicitud)}
        />
        <Column
          header="Solicitante"
          body={(s: SolicitudTrazabilidad) => (
            <button
              type="button"
              className="fd-link"
              title="Filtrar dashboard por este usuario"
              onClick={() => filtros.filterByUsuario(s.idUsuario, s.usuario)}
            >
              <span className="fd-prov-name">{clean(s.usuario) || "-"}</span>
              <span className="fd-prov-rut">{s.tipoDocumento}</span>
            </button>
          )}
        />
        <Column
          header="Centro costo"
          body={(s: SolicitudTrazabilidad) => {
            const ccs = (s.centrosCosto ?? []).map(clean);
            return ccs.length > 1
              ? `${ccs[0]} +${ccs.length - 1}`
              : ccs[0] || "-";
          }}
        />
        <Column
          header="Monto"
          body={(s: SolicitudTrazabilidad) => (
            <span className="fd-num">{formatMoney(s.montoClp)}</span>
          )}
        />
        <Column
          header="Estado"
          body={(s: SolicitudTrazabilidad) => <EstadoTag estado={s.estado} />}
        />
        <Column
          header="Etapa actual"
          body={(s: SolicitudTrazabilidad) => (
            <Tag
              value={formatEtapa(s.etapaActual)}
              severity={etapaSeverity(s.etapaActual)}
            />
          )}
        />
        <Column
          header="OC"
          body={(s: SolicitudTrazabilidad) => (
            <span className="fd-num">
              {(s.ordenes ?? []).map((o) => o.nroOrdenCompra).join(", ") || "-"}
            </span>
          )}
        />
        <Column
          header="Días a OC"
          body={(s: SolicitudTrazabilidad) => (
            <span className="fd-num">{formatDias(s.diasSolicitudAOrden)}</span>
          )}
        />
        <Column
          header="OC → INET"
          headerTooltip="Días desde la creación de la OC hasta su ingreso a INET (si hay varias OC, la más lenta)"
          body={(s: SolicitudTrazabilidad) => (
            <PlazoSolicitud ordenes={s.ordenes} calcular={plazoInet} />
          )}
        />
        <Column
          header="OC → recepción"
          headerTooltip="Días desde la creación de la OC hasta su primera recepción (si hay varias OC, la más lenta)"
          body={(s: SolicitudTrazabilidad) => (
            <PlazoSolicitud ordenes={s.ordenes} calcular={plazoRecepcion} />
          )}
        />
        <Column
          header="Días a factura"
          body={(s: SolicitudTrazabilidad) => (
            <span className="fd-num">
              {formatDias(s.diasSolicitudAFactura)}
            </span>
          )}
        />
      </DataTable>
    </ChartCard>
  );
}
